import dynamic from "next/dynamic";
import { MarketingCenteredText } from "../../../components/MarketingCenteredTextBlock";
import { MarketingFooter } from "../../../components/MarketingFooter";
import { MarketingGetStarted } from "../../../components/MarketingGetStarted";
import { MarketingMenu } from "../../../components/MarketingMenu";
import { MarketingOGTags } from "../../../components/MarketingOGTags";
import { MarketingTextBlock } from "../../../components/MarketingTextBlock";
import { ContentMarketersDesktopSVG, MarketingUseCaseHeader } from "../../../components/MarketingUseCaseHeader";
import { MarketingUserPledge } from "../../../components/MarketingUserPledge";
import { FadeUpContainer } from "../../../components/ReusableStyledComponents/FadeUp";
import { ScrollDownArrow } from "../../../components/ScrollDownArrow";
import { VerticalSpacing } from "../../../components/VerticalSpacing";
import { theme } from "../../../theme/theme";
import { APPLICATION_ROUTES } from "../../../shared-logic/routes";

const MarketingCarouselUseCases = dynamic(() => import("../../../components/MarketingCarouselUseCases/MarketingCarouselUseCases"), { ssr: false });

const urls = [
    "https://authory.com/CorinnaCornejo",
    "https://authory.com/JennChen",
    "https://authory.com/MarijanaKostelac",
    "http://authory.com/TomHallissey",
    "http://authory.com/RebeccaReynoso",
    "http://authory.com/AsiaBradlee",
    "http://authory.com/SarahCronk",
    "http://authory.com/EliseDopson",
    "http://authory.com/WillKelly",
    "http://authory.com/KeithReidCleveland",
    "http://authory.com/NickMoore"
];

export const ContentMarketersPageLayout = () => {
    return (
        <>
            <MarketingOGTags
                title="Authory – The magical portfolio for content marketers"
                description="Authory provides content marketers with an incredibly easy way to curate and showcase their work."
                canonicalUrl={`${process.env.NEXT_PUBLIC_FRONTEND_URL}${APPLICATION_ROUTES.USE_CASE_CONTENT_MARKETERS}`}
            />
            <MarketingMenu />
            <FadeUpContainer>
                <ScrollDownArrow
                    content={
                        <VerticalSpacing top={0} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 20 }]}>
                            <MarketingUseCaseHeader
                                title="Content Marketers"
                                description={
                                    <>
                                        “Authory really is a content marketer's dream! One aspect I dread about having a website is manually updating
                                        my portfolio. Now, Authory does that for me, saving me time and headspace while allowing me to maintain my
                                        brand.”
                                    </>
                                }
                                desktopImage={<ContentMarketersDesktopSVG />}
                                mobileImage={<></>}
                                userBio={
                                    <>
                                        <strong>- Alyssa Towns Swantkoski,</strong> Content Marketer
                                    </>
                                }
                                profileLink="https://authory.com/AlyssaTowns"
                            />
                        </VerticalSpacing>
                    }
                />
                <VerticalSpacing top={80}>
                    <MarketingCarouselUseCases
                        imagePath="/authory_static_files/images/use-cases/slider/content_marketers/content_marketers_"
                        urls={urls}
                        targetAudience="content marketers"
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingCenteredText
                        title={"Portfolio"}
                        punchline={
                            <>
                                Your <strong>magical business card</strong>
                            </>
                        }
                        description={
                            <>
                                <p>
                                    Authory makes your content look really good. No matter if your content is published under your name or not, with
                                    Authory you can compile it all into an effective portfolio that's just stunning to look at.
                                </p>
                                <p>
                                    It works with any type of content, from articles to podcasts, pictures or videos that you publish. And it's
                                    automatically updating whenever you publish something new. Why send anybody your LinkedIn profile if you have an
                                    interactive resume showing the content you've created that truly speaks for itself?
                                </p>
                            </>
                        }
                        href={APPLICATION_ROUTES.PORTFOLIO}
                        linkText={
                            <>
                                Learn more about <strong>Portfolio</strong>
                            </>
                        }
                        desktopIcon={<img src="/authory_static_files/images/icons/present_xl_active.svg" />}
                        mobileIcon={<img src="/authory_static_files/images/icons/present_m_active.svg" />}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingCenteredText
                        title={"Archive"}
                        iconPosition="right"
                        punchline={
                            <>
                                <strong>Know what you've published</strong> when & where
                            </>
                        }
                        description={
                            <>
                                <p>
                                    Often you won't know when exactly a client publishes your content. But just because your content is all over the
                                    place doesn't mean you need to be.
                                </p>
                                <p>
                                    Authory will always find your new pieces and add them to your account, fully automated for all your bylined work.
                                    Anything else can be added with two clicks. And thanks to the extensive search and filter features you can always
                                    check what you've created in the past within a few seconds only.
                                </p>
                                <p>That's why we've been called "my outboard memory" by our customers!</p>
                            </>
                        }
                        href={APPLICATION_ROUTES.ARCHIVE}
                        linkText={
                            <>
                                Learn more about <strong>Archive</strong>
                            </>
                        }
                        desktopIcon={<img src="/authory_static_files/images/icons/track_xl_active.svg" />}
                        mobileIcon={<img src="/authory_static_files/images/icons/track_m_active.svg" />}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingCenteredText
                        title={"Collections"}
                        punchline={
                            <>
                                Curate your work and <strong>show it to the right people</strong>
                            </>
                        }
                        description={
                            <>
                                <p>
                                    Want to apply for a new job/gig and need to present a selection of your work that matches the job requirements?
                                    The days of frantically searching for the right articles and then turning them into PDFs are over.
                                </p>
                                <p>
                                    With Authory, you can easily curate specific pieces into collections and then create special share links for
                                    these. It's never been easier to share examples of your past work while making sure they look first class at the
                                    recipient.
                                </p>
                            </>
                        }
                        href={APPLICATION_ROUTES.COLLECTIONS_MARKETING}
                        linkText={
                            <>
                                Learn more about <strong>Collections</strong>
                            </>
                        }
                        desktopIcon={<img src="/authory_static_files/images/icons/curate_xl_active.svg" />}
                        mobileIcon={<img src="/authory_static_files/images/icons/curate_m_active.svg" />}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingUserPledge
                        title={
                            <>
                                “Authory instantly infused my personal brand with credibility, has saved me countless hours of work, and{" "}
                                <strong>doubled demand for my services.</strong>”
                            </>
                        }
                        description={
                            <>
                                <strong>Gabriel Swain,</strong> Insightly, Agile CRM and more
                            </>
                        }
                        avatarImage={"/authory_static_files/images/pledges/Gabriel.jpg"}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingCenteredText
                        title={"Archive"}
                        iconPosition="right"
                        punchline={
                            <>
                                Make sure your work <strong>remains accessible</strong>
                            </>
                        }
                        description={
                            <>
                                <p>
                                    Writing for a corporate client means your content is published on their premises. And for reasons that you have no
                                    control over, your work might vanish anytime.
                                </p>
                                <p>
                                    Luckily, with Authory you'll always have a full text backup of everything you've ever created. Regardless of what
                                    happens to the original, once a piece of content is in your Authory account it's safe. And if the original is
                                    gone, you can even decide to make your private copy in your Authory account available to anybody visiting your
                                    Authory page.
                                </p>
                            </>
                        }
                        href={APPLICATION_ROUTES.ARCHIVE}
                        linkText={
                            <>
                                Learn more about <strong>Archive</strong>
                            </>
                        }
                        desktopIcon={<img src="/authory_static_files/images/icons/protect_xl_active.svg" />}
                        mobileIcon={<img src="/authory_static_files/images/icons/protect_m_active.svg" />}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingCenteredText
                        title={"Archive"}
                        punchline={
                            <>
                                <strong>Zero effort and zero tech skills</strong> required
                            </>
                        }
                        description={
                            <>
                                Setting up your Authory account takes about 3 minutes. And with that you don't need to do anything anymore. Authory
                                will automatically find all your past and your future work that's been published under your name. And adding anything
                                ghostwritten or without a byline at all takes a few second only. It could not be easier to finally track and have all
                                your content under one roof without having to worry about the upkeep.
                            </>
                        }
                        href={APPLICATION_ROUTES.ARCHIVE}
                        linkText={
                            <>
                                Learn more about <strong>Archive</strong>
                            </>
                        }
                        desktopIcon={<img src="/authory_static_files/images/icons/track_xl_active.svg" />}
                        mobileIcon={<img src="/authory_static_files/images/icons/track_m_active.svg" />}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingCenteredText
                        title={"Analytics"}
                        iconPosition="right"
                        punchline={
                            <>
                                <strong>Know your reach</strong> on social media
                            </>
                        }
                        description={
                            <>
                                <p>
                                    There are plenty of ways to find out what your social media accounts' reach is. Yet, do you know what reach your
                                    content has, independently from your accounts? Authory monitors and saves the social media interactions your
                                    content has received.{" "}
                                </p>
                                <p>
                                    Number of shares, likes, comments, tweets and more: You'll know for each and every piece that you've ever written
                                    how it's performed on social media. That applies to all past articles too.This can be a real asset when you are in
                                    talks with a client. Who else can provide bulletproof metrics about the content they've produced so far? You'll
                                    stand out like a star.
                                </p>
                            </>
                        }
                        href={APPLICATION_ROUTES.ANALYTICS_MARKETING}
                        linkText={
                            <>
                                Learn more about <strong>Analytics</strong>
                            </>
                        }
                        desktopIcon={<img src="/authory_static_files/images/icons/analyze_xl_active.svg" />}
                        mobileIcon={<img src="/authory_static_files/images/icons/analyze_m_active.svg" />}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingTextBlock
                        title={
                            <>
                                Your very own <strong>blog included</strong>
                            </>
                        }
                        description={
                            <>
                                <p>
                                    Sometimes you might not want to write somewhere else. You might want to publish on your own turf instead. Forget
                                    about having to set up a blog for this, or having to use a third party service with zero control like Medium.
                                </p>
                                <p>
                                    Authory comes with a built-in blogging feature, so you can publish your articles, pictures or videos right on your
                                    Authory page. Of course, out of the box it'll look as brilliant as everything else on your Authory page does.
                                </p>
                            </>
                        }
                        icon={<img src="/authory_static_files/images/icons/blog.svg" />}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120}>
                    <MarketingGetStarted />
                </VerticalSpacing>
                <VerticalSpacing top={120}>
                    <MarketingFooter />
                </VerticalSpacing>
            </FadeUpContainer>
        </>
    );
};
